import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { dateTime } from "@/lib/pricing";
import { apiMessage } from "@/lib/team";

const input = "w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:border-cyan-400 text-sm font-mono";
const strong = (pw) => pw.length >= 12 && [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length >= 3;

function readToken() {
  const t = new URLSearchParams(window.location.hash.slice(1)).get("token") || "";
  if (t) window.history.replaceState(null, "", window.location.pathname);  // keep the one-time link out of history
  return t;
}

/** Public page: an invited Cyannect team member chooses their own password. The link works once, for 72 hours. */
export default function AcceptInvite() {
  const [token] = useState(readToken);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(token ? "" : "This invitation link is incomplete. Open the link from your email again.");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.post("/auth/team-invite/check", { token }).then((r) => setInvite(r.data)).catch((e) => setError(apiMessage(e, "This invitation link is invalid or has expired.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/team-invite/accept", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(apiMessage(err, "Couldn't finish setting up your access."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 px-4" data-testid="accept-invite-page">
      <div className="relative w-full max-w-md p-8 rounded-2xl bg-slate-900/80 border border-slate-800 shadow-2xl">
        <div className="inline-flex items-center gap-2 text-cyan-400 text-[11px] uppercase tracking-widest font-semibold"><ShieldCheck className="w-4 h-4" /> Cyannect Team</div>
        <h1 className="font-display text-3xl tracking-tight mt-4" style={{ color: "#f8fafc" }}>{done ? "You're all set" : "Set up your access"}</h1>
        {!invite && !error && <div className="mt-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {error && <p className="mt-6 text-sm text-red-300 bg-red-950/50 rounded-lg px-3 py-2" role="alert" data-testid="accept-invite-error">{error}</p>}
        {done && (
          <div className="mt-6 space-y-4" data-testid="accept-invite-done">
            <p className="text-sm text-slate-300 flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Your password is saved. Sign in with {invite?.email}.</p>
            <Link to="/admin/login" className="block w-full text-center py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold transition-colors" data-testid="accept-invite-login">Go to sign in</Link>
          </div>
        )}
        {invite && !done && (
          <form onSubmit={submit} className="mt-4">
            <p className="text-sm text-slate-400">Hi <span className="text-slate-100" data-testid="accept-invite-name">{invite.name}</span> — you're joining as <span className="text-cyan-300" data-testid="accept-invite-role">{invite.role_label}</span>.</p>
            <p className="text-xs text-slate-500 mt-1 font-mono break-all" data-testid="accept-invite-email">{invite.email} · link expires {dateTime(invite.expires_at)}</p>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mt-6 mb-2">New password</label>
            <div className="relative">
              <input type="password" required autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={`${input} pl-10`} data-testid="accept-invite-password" />
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            </div>
            <p className={`text-[11px] mt-1 ${strong(pw) ? "text-emerald-400" : "text-slate-500"}`}>At least 12 characters with three of: lowercase, uppercase, number, symbol.</p>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mt-4 mb-2">Confirm password</label>
            <input type="password" required autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={input} data-testid="accept-invite-password-confirm" />
            {pw2 && pw !== pw2 && <p className="text-[11px] mt-1 text-red-300">Passwords don't match.</p>}
            <button type="submit" disabled={busy || !strong(pw) || pw !== pw2} className="w-full mt-6 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold transition-colors disabled:opacity-40" data-testid="accept-invite-submit">
              {busy ? "Saving…" : "Save password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
